import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTransition } from '../../utils/pageTransitions';

const UpdatePassword = () => {
    const { updatePassword, user } = useAuth();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const { variants, transition } = usePageTransition();

    // Redirect if not authenticated (meaning the recovery link wasn't valid or expired)
    // Supabase automatically logs the user in when they click the recovery link, so 'user' should be present.
    useEffect(() => {
        // Small timeout to allow auth state to settle might be needed, but usually user is set quickly.
        // However, if user visits this page directly without a valid session, they should not be here.
        // For now we'll handle the logic in the submit. If connection fails, error will show.
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            return setError('Les mots de passe ne correspondent pas.');
        }

        setLoading(true);
        setError('');
        setMessage('');

        try {
            if (!user) {
                throw new Error("Session invalide ou expirée. Veuillez redemander la réinitialisation.");
            }
            await updatePassword(password);
            setMessage('Mot de passe mis à jour avec succès. Vous allez être redirigé vers le Dashboard...');
            setTimeout(() => {
                navigate('/protected-admin-dashboard'); // Or admin-login
            }, 3000);
        } catch (err) {
            setError(err.message || 'Erreur lors de la mise à jour du mot de passe.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 flex items-center justify-center p-4"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
            transition={transition}
        >
            <Helmet>
                <title>Nouveau Mot de Passe - ORAZEK Investment</title>
            </Helmet>
            <div className="bg-card rounded-2xl shadow-premium-xl w-full max-w-md border border-border">
                <div className="p-6 md:p-8">
                    <div className="flex items-center justify-center mb-8">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <Icon name="LockKeyhole" size={32} color="var(--color-primary)" />
                            </div>
                            <h1 className="text-2xl font-bold text-foreground mb-2">Nouveau mot de passe</h1>
                            <p className="text-sm text-muted-foreground">Créez votre nouveau mot de passe sécurisé.</p>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2 text-error text-sm">
                            <Icon name="AlertCircle" size={20} className="flex-shrink-0 mt-0.5" />
                            <p>{error}</p>
                        </div>
                    )}

                    {message && (
                        <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-2 text-success text-sm">
                            <Icon name="CheckCircle" size={20} className="flex-shrink-0 mt-0.5" />
                            <p>{message}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <Input
                            type="password"
                            label="Nouveau Mot de Passe"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                        />
                        <Input
                            type="password"
                            label="Confirmer le Mot de Passe"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={6}
                        />

                        <Button
                            type="submit"
                            variant="default"
                            fullWidth
                            disabled={loading}
                            iconName="Save"
                            iconPosition="right"
                        >
                            {loading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
                        </Button>
                    </form>
                </div>
            </div>
        </motion.div>
    );
};

export default UpdatePassword;
